import { lazy, Suspense } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';

import { RouteLoadingFallback } from '@/components/loading/RouteLoadingFallback';
import { AppLayout } from '@/layouts/AppLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { NotFoundPage } from '@/pages/not-found/NotFoundPage';
import { ErrorBoundary } from '@/routes/ErrorBoundary';
import { RequireAuth } from '@/routes/guards/RequireAuth';
import { RequirePermission } from '@/routes/guards/RequirePermission';
import { routePaths } from '@/routes/paths';

const LoginPage = lazy(() =>
  import('@/pages/login/LoginPage').then(module => ({ default: module.LoginPage }))
);
const ForgotPasswordPage = lazy(() =>
  import('@/pages/forgot-password/ForgotPasswordPage').then(module => ({ default: module.ForgotPasswordPage }))
);
const ResetPasswordPage = lazy(() =>
  import('@/pages/reset-password/ResetPasswordPage').then(module => ({ default: module.ResetPasswordPage }))
);
const DashboardPage = lazy(() =>
  import('@/pages/dashboard/DashboardPage').then(module => ({ default: module.DashboardPage }))
);
const GettingStartedPage = lazy(() =>
  import('@/pages/getting-started/GettingStartedPage').then(module => ({ default: module.GettingStartedPage }))
);
const PatientsPage = lazy(() =>
  import('@/pages/patients/PatientsPage').then(module => ({ default: module.PatientsPage }))
);
const PatientDetailPage = lazy(() =>
  import('@/pages/patient-detail/PatientDetailPage').then(module => ({
    default: module.PatientDetailPage
  }))
);
const PatientHistoryPage = lazy(() =>
  import('@/pages/patient-history/PatientHistoryPage').then(module => ({
    default: module.PatientHistoryPage
  }))
);
const PatientRegistrationPage = lazy(() =>
  import('@/pages/patient-registration/PatientRegistrationPage').then(module => ({
    default: module.PatientRegistrationPage
  }))
);
const QuickPatientPage = lazy(() =>
  import('@/pages/patients/QuickPatientPage').then(module => ({
    default: module.QuickPatientPage
  }))
);
const SchedulePage = lazy(() =>
  import('@/pages/schedule/SchedulePage').then(module => ({ default: module.SchedulePage }))
);
const NewAppointmentPage = lazy(() =>
  import('@/pages/schedule/NewAppointmentPage').then(module => ({ default: module.NewAppointmentPage }))
);
const InvoicesPage = lazy(() =>
  import('@/pages/invoices/InvoicesPage').then(module => ({ default: module.InvoicesPage }))
);
const NewInvoicePage = lazy(() =>
  import('@/pages/invoices/NewInvoicePage').then(module => ({ default: module.NewInvoicePage }))
);
const MastersPage = lazy(() =>
  import('@/pages/masters/MastersPage').then(module => ({ default: module.MastersPage }))
);
const MasterMenuPage = lazy(() =>
  import('@/pages/masters/MasterMenuPage').then(module => ({ default: module.MasterMenuPage }))
);
const DoctorsMasterPage = lazy(() =>
  import('@/pages/masters/DoctorsMasterPage').then(module => ({ default: module.DoctorsMasterPage }))
);
const ProceduresMasterPage = lazy(() =>
  import('@/pages/masters/ProceduresMasterPage').then(module => ({ default: module.ProceduresMasterPage }))
);
const AppointmentTypesMasterPage = lazy(() =>
  import('@/pages/masters/AppointmentTypesMasterPage').then(module => ({
    default: module.AppointmentTypesMasterPage
  }))
);
const AdminMenuPage = lazy(() =>
  import('@/pages/admin/AdminMenuPage').then(module => ({ default: module.AdminMenuPage }))
);
const ClinicProfileAdminPage = lazy(() =>
  import('@/pages/admin/ClinicProfileAdminPage').then(module => ({ default: module.ClinicProfileAdminPage }))
);
const ImportPage = lazy(() =>
  import('@/pages/admin/ImportPage').then(module => ({ default: module.ImportPage }))
);
const RolesPermissionsPage = lazy(() =>
  import('@/pages/admin/RolesPermissionsPage').then(module => ({ default: module.RolesPermissionsPage }))
);
const RoleEditorPage = lazy(() =>
  import('@/pages/admin/RoleEditorPage').then(module => ({ default: module.RoleEditorPage }))
);
const NotificationDeliveryAdminPage = lazy(() =>
  import('@/pages/admin/NotificationDeliveryAdminPage').then(module => ({ default: module.NotificationDeliveryAdminPage }))
);
const UsersAdminPage = lazy(() =>
  import('@/pages/admin/UsersAdminPage').then(module => ({ default: module.UsersAdminPage }))
);
const ReportsPage = lazy(() =>
  import('@/pages/reports/ReportsPage').then(module => ({ default: module.ReportsPage }))
);
const ReportMetricPage = lazy(() =>
  import('@/pages/reports/ReportMetricPage').then(module => ({ default: module.ReportMetricPage }))
);
const AssistantPage = lazy(() =>
  import('@/pages/assistant/AssistantPage').then(module => ({ default: module.AssistantPage }))
);
const ProfilePage = lazy(() =>
  import('@/pages/profile/ProfilePage').then(module => ({ default: module.ProfilePage }))
);
const OwnerPage = lazy(() =>
  import('@/pages/owner/OwnerPage').then(module => ({ default: module.OwnerPage }))
);
const ErrorsPage = lazy(() =>
  import('@/pages/errors/ErrorsPage').then(module => ({ default: module.ErrorsPage }))
);
const SystemHealthPage = lazy(() =>
  import('@/pages/system-health/SystemHealthPage').then(module => ({ default: module.SystemHealthPage }))
);

const AppRoutes = () =>
  useRoutes([
    {
      path: routePaths.root,
      element: <Navigate to={routePaths.app.dashboard} replace />
    },
    {
      path: routePaths.auth.root,
      element: <AuthLayout />,
      children: [
        { path: routePaths.auth.login, element: <LoginPage /> },
        { path: routePaths.auth.forgotPassword, element: <ForgotPasswordPage /> },
        { path: routePaths.auth.resetPassword, element: <ResetPasswordPage /> }
      ]
    },
    {
      path: routePaths.app.root,
      element: (
        <RequireAuth>
          <AppLayout />
        </RequireAuth>
      ),
      children: [
        { path: routePaths.app.dashboard, element: <DashboardPage /> },
        { path: routePaths.app.gettingStarted, element: <GettingStartedPage /> },
        { path: routePaths.app.patients, element: <PatientsPage /> },
        { path: routePaths.app.patientQuickAdd, element: <QuickPatientPage /> },
        { path: routePaths.app.patientNew, element: <PatientRegistrationPage /> },
        { path: routePaths.app.patientDetails, element: <PatientDetailPage /> },
        { path: routePaths.app.patientHistory, element: <PatientHistoryPage /> },
        { path: routePaths.app.patientEdit, element: <PatientRegistrationPage /> },
        { path: routePaths.app.schedule, element: <SchedulePage /> },
        { path: routePaths.app.scheduleNew, element: <NewAppointmentPage /> },
        { path: routePaths.app.invoices, element: <InvoicesPage /> },
        { path: routePaths.app.invoiceNew, element: <NewInvoicePage /> },
        { path: routePaths.app.invoiceEdit, element: <NewInvoicePage /> },
        {
          path: routePaths.app.masters,
          element: (
            <RequirePermission permission={['setup.access', 'masters.read']}>
              <MastersPage />
            </RequirePermission>
          )
        },
        {
          path: routePaths.app.mastersMenu,
          element: (
            <RequirePermission permission={['setup.access', 'masters.read']}>
              <MasterMenuPage />
            </RequirePermission>
          )
        },
        {
          path: routePaths.app.mastersDoctors,
          element: (
            <RequirePermission permission={['setup.access', 'masters.read']}>
              <DoctorsMasterPage />
            </RequirePermission>
          )
        },
        {
          path: routePaths.app.mastersProcedures,
          element: (
            <RequirePermission permission={['setup.access', 'masters.read']}>
              <ProceduresMasterPage />
            </RequirePermission>
          )
        },
        {
          path: routePaths.app.mastersAppointmentTypes,
          element: (
            <RequirePermission permission={['setup.access', 'masters.read']}>
              <AppointmentTypesMasterPage />
            </RequirePermission>
          )
        },
        {
          path: routePaths.app.adminMenu,
          element: (
            <RequirePermission permission="users.manage">
              <AdminMenuPage />
            </RequirePermission>
          )
        },
        {
          path: routePaths.app.adminClinicProfile,
          element: (
            <RequirePermission permission="users.manage">
              <ClinicProfileAdminPage />
            </RequirePermission>
          )
        },
        {
          path: routePaths.app.adminImport,
          element: (
            <RequirePermission permission="users.manage">
              <ImportPage />
            </RequirePermission>
          )
        },
        {
          path: routePaths.app.adminRoles,
          element: (
            <RequirePermission permission="users.manage">
              <RolesPermissionsPage />
            </RequirePermission>
          )
        },
        {
          path: routePaths.app.adminRoleNew,
          element: (
            <RequirePermission permission="users.manage">
              <RoleEditorPage />
            </RequirePermission>
          )
        },
        {
          path: routePaths.app.adminRoleDetails,
          element: (
            <RequirePermission permission="users.manage">
              <RoleEditorPage />
            </RequirePermission>
          )
        },
        {
          path: routePaths.app.adminNotificationDeliveries,
          element: (
            <RequirePermission permission="users.manage">
              <NotificationDeliveryAdminPage />
            </RequirePermission>
          )
        },
        {
          path: routePaths.app.adminUsers,
          element: (
            <RequirePermission permission="users.manage">
              <UsersAdminPage />
            </RequirePermission>
          )
        },
        { path: routePaths.app.reports, element: <ReportsPage /> },
        { path: routePaths.app.reportMetric, element: <ReportMetricPage /> },
        { path: routePaths.app.assistant, element: <AssistantPage /> },
        { path: routePaths.app.profile, element: <ProfilePage /> },
        {
          path: routePaths.app.owner,
          element: (
            <RequirePermission permission="users.manage">
              <OwnerPage />
            </RequirePermission>
          )
        },
        {
          path: routePaths.app.errors,
          element: (
            <RequirePermission permission="users.manage">
              <ErrorsPage />
            </RequirePermission>
          )
        },
        {
          path: routePaths.app.systemHealth,
          element: (
            <RequirePermission permission="users.manage">
              <SystemHealthPage />
            </RequirePermission>
          )
        }
      ]
    },
    {
      path: '*',
      element: <NotFoundPage />
    }
  ]);

export function AppRouter() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteLoadingFallback />}>
        <AppRoutes />
      </Suspense>
    </ErrorBoundary>
  );
}
